import { useEffect, useState } from 'react';
import { Modal } from '@/components/utils/Modal';
import Select, { type StylesConfig } from 'react-select';
import type { GetSendToElements } from '@/types/dashboard';
import { dashboardService } from '@/services/dashboardService';
import { getSideMenuLocal } from '@/components/utils/CorgeeHelper';
import { dashboardGroupsService, type DashboardGroupData } from '@/components/DashboardGroups';
import { toNumber } from 'lodash';
import { dashboardGroupService } from '@/services/dashboardGroupService';

const comboBoxSelectStyles: StylesConfig<any, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    borderColor: state.isFocused ? '#66afe9' : '#ccc',
    '&:hover': { borderColor: '#66afe9' },
  }),
  valueContainer: base => ({
    ...base,
    height: '32px',
    padding: '0 8px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    lineHeight: 'normal',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, padding: '4px 6px' }),
  clearIndicator: base => ({ ...base, padding: '4px' }),
  placeholder: base => ({
    ...base,
    fontSize: '12px',
    lineHeight: 'normal',
    color: '#aaa',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 'normal',
    color: 'rgb(158, 149, 135)',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1000200,
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '200px',
    fontSize: '10px',
    backgroundColor: '#fff',
  }),
  option: (_base, state) => ({
    display: 'block',
    boxSizing: 'border-box' as const,
    width: '100%',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: '1.4',
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#62accb' : '#fff',
    color: state.isFocused ? '#fff' : '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ':hover': {
      backgroundColor: '#00000005',
    },
  }),
};

type moveItem = {
  Value: number;
  Text: string | undefined;
  Type: string;
};
type moveTo = {
  currentMoveTo: moveItem | null;
  options: moveItem[];
};

type dashboardItem = {
  Value: number;
  Text: string;
  DashboardGroups: any[];
  ConfigValueId: number;
};

type dashboardTo = {
  dashboardMoveTo: dashboardItem | null;
  options: dashboardItem[];
};

type MoveCopyToModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdateDashboardGroupPosition: (removedDashboardGroupId: number) => void;
  SocketConnectionId?: string;
  dashboardGroupOrigin: DashboardGroupData;
  isMoveTo: boolean;
  projectId?: string;
  configValueId?: number;
};

const MoveCopyToDashboardGroupModal = ({
  open,
  setOpen,
  onUpdateDashboardGroupPosition,
  projectId,
  dashboardGroupOrigin,
  isMoveTo,
}: MoveCopyToModalProps) =>
{
  const getConfigValue = () =>
  {
    const path = window.location.pathname;
    if (path.includes('dashboard'))
    {
      return parseInt(path.split('/').pop()!);
    }
    else return 1;
  };

  function getConfigValueName(configValueId: number): string
  {
    const sideMenus = getSideMenuLocal();
    if (!sideMenus) return '';
    for (let i = 0; i < (sideMenus?.length ?? 0); i++)
    {
      if (sideMenus![i]?.ConfigValueID == configValueId) return sideMenus![i]?.Text ?? '';
    }
    return '';
  }

  let SelectedDashboard: dashboardItem | null | undefined;
  let _SelectedMoveTo: moveItem | Record<string, unknown>;
  void _SelectedMoveTo!;
  let _SelectedGroup: any;
  void _SelectedGroup;
  let DashboardGroups: any[];
  void DashboardGroups!;
  let _itemsCount: number;
  void _itemsCount!;

  const [loading, setLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [moveTo, setMoveTo] = useState<moveTo>();
  const [dashboardsMoveTo, setDashboardsMoveTo] = useState<dashboardTo>();
  const [title, setTitle] = useState<string>(isMoveTo ? 'Move to' : 'Copy to');

  const close = () =>
  {
    setOpen(false);
    setLoading(false);
  };

  useEffect(() =>
  {
    if (!loading) initial();
  }, []);

  useEffect(() =>
  {
    onChange(1);
  }, [dashboardsMoveTo?.dashboardMoveTo]);

  const fetchGetDashboards = async (
    configValueId: number,
    ProjectId: number,
    organizeDashboard?: boolean,
  ): Promise<any[]> =>
  {
    setLoading(true);
    try
    {
      const response = await dashboardService.getDashboardsByConfigValue(
        configValueId,
        ProjectId || undefined,
        organizeDashboard || false,
      );
      return response ?? [];
    }
    catch (error)
    {
      console.error('Error loading organize dashboards:', error);
      return [];
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleChangeMoveTo = (selectedOption: moveItem | null) =>
  {
    if (selectedOption)
    {
      setMoveTo({
        currentMoveTo: selectedOption,
        options: moveTo?.options ?? [],
      });
      onChange(0, selectedOption);
    }
    else
    {
      setMoveTo({
        currentMoveTo: null,
        options: moveTo?.options ?? [],
      });
      handleChangeDashboardTo(null);
      setDashboardsMoveTo({
        dashboardMoveTo: null,
        options: [],
      });
    }
  };

  const handleChangeDashboardTo = (selectedOption: dashboardItem | null) =>
  {
    if (selectedOption)
    {
      setDashboardsMoveTo({
        dashboardMoveTo: selectedOption,
        options: dashboardsMoveTo?.options ?? [],
      });
      onChange(1);
    }
    else
    {
      setDashboardsMoveTo({
        dashboardMoveTo: null,
        options: dashboardsMoveTo?.options ?? [],
      });
    }
  };

  const initial = () =>
  {
    setTitle(isMoveTo ? 'Move to' : 'Copy to');

    dashboardGroupsService
      .GetSendToElements(dashboardGroupOrigin.DashboardGroupId)
      .then(function (response)
      {
        const moveToItems = response;
        let selectedMoveTo = {};
        moveToItems.push({ Value: 1, Text: getConfigValueName(1), Type: 'CONFIGVALUE' });
        if (projectId && moveToItems != undefined)
        {
          for (let i = 0; i < moveToItems.length; i++)
          {
            if (moveToItems[i]!.Type == 'PROJECT' && moveToItems[i]!.Value == toNumber(projectId))
            {
              selectedMoveTo = {
                Value: moveToItems[i]!.Value,
                Text: moveToItems[i]!.Text,
                Type: 'PROJECT',
              };
              break;
            }
          }
        }
        else
        {
          selectedMoveTo = {
            Value: getConfigValue(),
            Text: getConfigValueName(getConfigValue()),
            Type: 'CONFIGVALUE',
          };
        }

        setMoveTo({
          currentMoveTo: selectedMoveTo as moveItem,
          options: moveToItems as GetSendToElements[],
        });

        onChange(0, selectedMoveTo);
      });
  };

  const onChange = async (level: number, selectedItem?: any) =>
  {
    let response: any[] = [];
    let dashboard: any;
    const dashboardsToPush: dashboardItem[] = [];
    switch (level)
    {
      case 0: // Main input select
        SelectedDashboard = null;
        DashboardGroups = [];
        _SelectedGroup = null;
        if (!selectedItem) return;

        const currentDashboardGroupId = dashboardGroupOrigin.DashboardGroupId;
        SelectedDashboard = null;

        _itemsCount = 0;

        switch (selectedItem.Type)
        {
          case 'PROJECT':
            response = await fetchGetDashboards(1, selectedItem.Value);
            // get DashboardId
            dashboard = response
              .flatMap(x => x.DashboardGroups)
              .find(x => x.DashboardGroupId == currentDashboardGroupId);

            for (let i = 0; i < response.length; i++)
            {
              for (let j = 0; j < (moveTo?.options?.length ?? 0); j++)
              {
                const moveToElement = moveTo?.options?.[j];
                if (
                  moveToElement &&
                  (moveToElement as any).Type == 'CONFIGVALUE' &&
                  moveToElement.Value != 1
                )
                {
                  if (
                    response[i].ConfigValueId == moveToElement.Value &&
                    response[i].IsDefault &&
                    response[i].FakeDashboardType == 0
                  )
                  {
                    dashboardsToPush.push({
                      Value: response[i].DashboardId,
                      Text: response[i].Name,
                      DashboardGroups: response[i].DashboardGroups,
                      ConfigValueId: response[i].ConfigValueId,
                    });
                  }
                }
              }
              if (response[i].ConfigValueId == 1 && response[i].FakeDashboardType == 0)
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }

              if (dashboard.DashboardId == response[i].DashboardId)
              {
                SelectedDashboard = {
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                };
              }
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });
            break;
          case 'CONFIGVALUE':
            response = await fetchGetDashboards(selectedItem.Value, -1);
            dashboard = (response as any[])
              .flatMap((x: any) => x.DashboardGroups)
              .find((x: any) => x.DashboardGroupId == currentDashboardGroupId);

            for (var i = 0; i < response.length; i++)
            {
              if (
                response[i].ConfigValueId &&
                (response[i].ConfigValueId == 1 ||
                  response[i].ConfigValueId == selectedItem.Value) &&
                response[i].FakeDashboardType == 0
              )
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }
              SelectedDashboard = {
                Value: response[i].DashboardId,
                Text: response[i].Name,
                DashboardGroups: response[i].DashboardGroups,
                ConfigValueId: response[i].ConfigValueId,
              };
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });

            break;
          case 'ORGANIZE':
            response = await fetchGetDashboards(selectedItem.Value, -1, true);
            for (var i = 0; i < response.length; i++)
            {
              if (
                response[i].ConfigValueId &&
                (response[i].ConfigValueId == 1 ||
                  response[i].ConfigValueId == selectedItem.Value) &&
                response[i].FakeDashboardType == 4
              )
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }
              SelectedDashboard = {
                Value: response[i].DashboardId,
                Text: response[i].Name,
                DashboardGroups: response[i].DashboardGroups,
                ConfigValueId: response[i].ConfigValueId,
              };
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });
            break;
          default:
            break;
        }
        setDashboardsMoveTo({
          options: dashboardsToPush,
          dashboardMoveTo: SelectedDashboard ?? null,
        });

        break;
      case 1: // Dashboard select
        _SelectedGroup = null;
        if (!selectedItem) return;

        setDashboardsMoveTo({
          dashboardMoveTo: selectedItem,
          options: [],
        });
    }
  };

  const submit = () =>
  {
    // to new Dashboard , check is not the same dashboard, then check duplicates and then move/copy
    if (
      dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[0]?.DashboardId != undefined &&
      dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[0]?.DashboardId !=
        dashboardGroupOrigin.DashboardId
    )
    {
      dashboardGroupsService
        .GetConversationsDuplicated(
          dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[0]?.DashboardId || 0,
          dashboardGroupOrigin.DashboardGroupId,
          '',
        )
        .then(function (areDuplicates)
        {
          if (areDuplicates == false)
          {
            dashboardGroupsService
              .AddDashboardGroupByParametersValidateCompatibleItems(
                dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[0]?.DashboardId || 0,
                dashboardGroupOrigin.DashboardGroupId,
                '0',
                0,
                true,
                dashboardsMoveTo?.dashboardMoveTo?.ConfigValueId || 1,
                toNumber(projectId),
                0,
                dashboardGroupOrigin.DashboardGroupId,
              )
              .then(async function (_response)
              {
                if (isMoveTo)
                {
                  await dashboardGroupService.deleteDashboardGroup(
                    dashboardGroupOrigin.DashboardGroupId,
                    '',
                  );
                  onUpdateDashboardGroupPosition(dashboardGroupOrigin.DashboardGroupId);
                }
                close();
              })
              .catch(function (error)
              {
                console.error('Error moving/copying item:', error);
                close();
              });
          }
          else
          {
            handleDuplicateWarningModal();
          }
        });
    }
    else
    {
      handleDuplicateWarningModal();
    }
  };

  const handleDuplicateWarningModal = () =>
  {
    setDuplicateWarning(true);
    setTitle('Duplicated Items');
  };

  return (
    <Modal open={open} onClose={() => close()} title={title} preventCloseClickOut>
      {!duplicateWarning ? (
        <div className="w-96">
          <div className="gap-4">
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px]">{title}:</div>
              <div className="flex-1">
                <Select
                  options={moveTo?.options}
                  value={moveTo?.currentMoveTo}
                  onChange={handleChangeMoveTo}
                  isSearchable={true}
                  isClearable={true}
                  getOptionLabel={(opt: moveItem) => opt.Text ?? ''}
                  className="w-full"
                  styles={comboBoxSelectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>
            </div>
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px]">Dashboards:</div>
              <div className="flex-1">
                <Select
                  isDisabled={dashboardsMoveTo?.options?.length === 0}
                  onChange={handleChangeDashboardTo}
                  value={dashboardsMoveTo?.options.find(
                    x => x.Value === dashboardsMoveTo?.dashboardMoveTo?.Value,
                  )}
                  options={dashboardsMoveTo?.options}
                  isSearchable={true}
                  isClearable={true}
                  getOptionLabel={(opt: dashboardItem) => opt.Text}
                  className="w-full"
                  styles={comboBoxSelectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>
            </div>
          </div>
          {
            <div
              style={{
                margin: '12px 0',
                padding: '4px 12px',
                backgroundColor: '#dcf3f7',
                color: 'black',
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
              Warning: Items with different project or item type, will not be moved.
            </div>
          }

          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              disabled={loading}
              onClick={submit}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={() => close()}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="w-128">
          <div className="gap-4">
            <div className="flex">
              <div className="flex-none text-sm align-middle leading-[45px]">
                {'   Some of the items you want to move are already in the dashboard.'}
              </div>
            </div>
          </div>

          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              disabled={loading}
              onClick={() => close()}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => close()}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
            >
              No
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MoveCopyToDashboardGroupModal;
